import { Offcanvas } from "bootstrap";

const SIDEBAR_OFFCANVAS_IDS = new Set(["sideMenu", "offcanvasNavbar"]);
const BOOTSTRAP_FIXED_CONTENT_SELECTOR =
  ".fixed-top, .fixed-bottom, .is-fixed, .sticky-top";
const BOOTSTRAP_STICKY_SELECTOR = ".sticky-top";

function shouldPreserveBodyOverflowLock() {
  const body = document.body;
  if (body.hasAttribute("data-rr-ui-modal-open")) {
    return true;
  }
  if (document.querySelector("#overlays .modal.show")) {
    return true;
  }
  return false;
}

/**
 * Fully release Bootstrap ScrollBarHelper body lock left behind when an offcanvas
 * hide lifecycle is interrupted (e.g. React Router navigation unmounts Header).
 *
 * Clears data-bs-* snapshots first so a late ScrollBarHelper.reset() cannot
 * re-apply overflow:hidden from a saved "hidden" value.
 *
 * Preserves body overflow when another modal (react-bootstrap or PeModal) is open.
 */
export function releaseBootstrapBodyScrollLock(options = {}) {
  const preserveModalLock =
    options.preserveModalLock ?? shouldPreserveBodyOverflowLock();
  const body = document.body;

  body.removeAttribute("data-bs-overflow");
  if (!preserveModalLock) {
    body.style.removeProperty("overflow");
  }

  body.removeAttribute("data-bs-padding-right");
  if (!preserveModalLock) {
    body.style.removeProperty("padding-right");
    body.style.removeProperty("padding");
  }

  document.querySelectorAll(BOOTSTRAP_FIXED_CONTENT_SELECTOR).forEach((element) => {
    element.removeAttribute("data-bs-padding-right");
    if (!preserveModalLock) {
      element.style.removeProperty("padding-right");
    }
  });

  document.querySelectorAll(BOOTSTRAP_STICKY_SELECTOR).forEach((element) => {
    element.removeAttribute("data-bs-margin-right");
    if (!preserveModalLock) {
      element.style.removeProperty("margin-right");
    }
  });

  if (!preserveModalLock) {
    body.classList.remove("modal-open");
  }

  document.querySelectorAll(".offcanvas-backdrop, .modal-backdrop").forEach((backdrop) => {
    if (backdrop.classList.contains("offcanvas-backdrop")) {
      backdrop.remove();
      return;
    }
    if (!preserveModalLock) {
      backdrop.remove();
    }
  });
}

/**
 * Release Bootstrap scroll lock only when it is orphaned — not while a visible
 * overlay on another part of the page still legitimately owns body scroll.
 */
export function releaseOrphanedBootstrapBodyScrollLock() {
  const body = document.body;

  let openTrackedOffcanvas = false;
  SIDEBAR_OFFCANVAS_IDS.forEach((id) => {
    const element = document.getElementById(id);
    if (element?.classList.contains("show")) {
      openTrackedOffcanvas = true;
    }
  });

  const hasBootstrapLockMarker =
    body.hasAttribute("data-bs-overflow") || body.hasAttribute("data-bs-padding-right");

  const visibleBootstrapOverlay = document.querySelector(
    ".offcanvas.show, .modal.show.d-block"
  );
  const preserveModalLock = shouldPreserveBodyOverflowLock();

  const hasOrphanBodyLock =
    body.classList.contains("modal-open") &&
    !visibleBootstrapOverlay &&
    !preserveModalLock;

  if (
    openTrackedOffcanvas ||
    hasBootstrapLockMarker ||
    (hasOrphanBodyLock && body.style.overflow === "hidden")
  ) {
    releaseBootstrapBodyScrollLock({ preserveModalLock });
  }
}

/**
 * Remove leftover offcanvas/modal backdrop and body classes after Bootstrap hide.
 * @deprecated Prefer releaseBootstrapBodyScrollLock — kept as alias for call sites.
 */
export function cleanupOffcanvasBackdrop() {
  releaseBootstrapBodyScrollLock();
}

/**
 * Sync body/backdrop state after Bootstrap finishes offcanvas hide, including
 * ScrollBarHelper.reset(). Running on hidden.bs.offcanvas avoids racing reset().
 */
export function bindOffcanvasBackdropCleanup() {
  const handleOffcanvasHidden = (event) => {
    const targetId = event.target?.id;
    if (targetId && SIDEBAR_OFFCANVAS_IDS.has(targetId)) {
      releaseBootstrapBodyScrollLock();
    }
  };

  document.addEventListener("hidden.bs.offcanvas", handleOffcanvasHidden);

  return () => {
    document.removeEventListener("hidden.bs.offcanvas", handleOffcanvasHidden);
  };
}

/**
 * Run Bootstrap Offcanvas.hide() and invoke callback after hidden.bs.offcanvas.
 * If the offcanvas is not open, runs callback immediately after defensive cleanup.
 */
async function invokeDismissCallback(callback) {
  try {
    await callback?.();
  } catch (error) {
    console.error("[offcanvas] dismiss callback failed:", error);
  }
}

export function dismissOffcanvasThen(elementOrId, callback) {
  const element =
    typeof elementOrId === "string"
      ? document.getElementById(elementOrId)
      : elementOrId;

  if (!element) {
    void invokeDismissCallback(callback);
    return;
  }

  if (!element.classList.contains("show")) {
    releaseOrphanedBootstrapBodyScrollLock();
    void invokeDismissCallback(callback);
    return;
  }

  const instance = Offcanvas.getOrCreateInstance(element);

  const onHidden = () => {
    element.removeEventListener("hidden.bs.offcanvas", onHidden);
    releaseBootstrapBodyScrollLock();
    void invokeDismissCallback(callback);
  };

  element.addEventListener("hidden.bs.offcanvas", onHidden);
  instance.hide();
}

/**
 * Intercept in-offcanvas link clicks while an offcanvas is open: dismiss via
 * Bootstrap, then navigate after hidden.bs.offcanvas completes.
 */
export function bindOffcanvasLinkNavigation(offcanvasElement, navigateFn) {
  if (!offcanvasElement || typeof navigateFn !== "function") {
    return () => {};
  }

  const handleClick = (event) => {
    const link = event.target.closest("a[href]");
    if (!link || !offcanvasElement.contains(link)) {
      return;
    }

    const href = link.getAttribute("href");
    if (!href || href === "#" || href.startsWith("http://") || href.startsWith("https://")) {
      return;
    }

    if (!offcanvasElement.classList.contains("show")) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    dismissOffcanvasThen(offcanvasElement, () => {
      navigateFn(href);
    });
  };

  offcanvasElement.addEventListener("click", handleClick);

  return () => {
    offcanvasElement.removeEventListener("click", handleClick);
  };
}

/** @deprecated Use bindOffcanvasLinkNavigation */
export function bindSidebarLinkNavigation(sideMenuElement, navigateFn) {
  return bindOffcanvasLinkNavigation(sideMenuElement, navigateFn);
}

/**
 * Bind link-navigation dismiss for all header offcanvas panels (#sideMenu,
 * #offcanvasNavbar). Safe when one panel is absent (e.g. logged-out buyer).
 */
export function bindHeaderOffcanvasNavigation(navigateFn) {
  const unbinders = [];

  SIDEBAR_OFFCANVAS_IDS.forEach((id) => {
    const element = document.getElementById(id);
    if (element) {
      unbinders.push(bindOffcanvasLinkNavigation(element, navigateFn));
    }
  });

  return () => {
    unbinders.forEach((unbind) => unbind());
  };
}

/**
 * Install offcanvas backdrop cleanup, in-panel navigation guards, and an
 * unmount safety net for interrupted Bootstrap scroll-lock lifecycles.
 */
export function bindHeaderOffcanvasLifecycle(navigateFn) {
  const unbindCleanup = bindOffcanvasBackdropCleanup();
  const unbindNav = bindHeaderOffcanvasNavigation(navigateFn);

  return () => {
    unbindCleanup();
    unbindNav();
    releaseOrphanedBootstrapBodyScrollLock();
  };
}
